/** @module csi/view/Widget */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/event",
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/dom",
	"dojo/dom-attr",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/promise/all",
	"dojo/topic",
	"dojo/when",
	"dojo/window",
	"dijit/_TemplatedMixin",
	"dijit/_Widget",
	"dijit/_WidgetsInTemplateMixin",
	"dijit/form/Button",
	"dijit/registry",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/DeferredTracking",
	"csi/_base/underscore",
	"csi/access/_base",
	"csi/action",
	"csi/comm",
	"csi/constants",
	"csi/core/extendedPathService",
	"csi/core/Key",
	"csi/core/SpecBuilder",
	"csi/def",
	"csi/def/Context",
	"csi/heading/_base",
	"csi/heading/Specs",
	"csi/html",
	"csi/locale",
	"csi/message",
	"csi/middleware/common",
	"csi/prefs",
	"csi/reflection",
	"csi/resolver",
	"csi/middleware/attributes/attributeKeysService",
	"csi/middleware/rollup/rollupService",
	"csi/service/openTelemetryService",
	"csi/service/viewService",
	"csi/service/writerHoldService",
	"csi/session",
	"csi/sessionConstants",
	"csi/state",
	"csi/state/rootStore",
	"csi/store/_base",
	"csi/store/Names",
	"csi/store/UserNames",
	"csi/view",
	"csi/view/util",
	"csi/widget/_HighlightMixin",
	"csi/widget/CheckboxDialogAction",
	"csi/widget/Tooltip",
	"csi/xml",
	"./Tooltip",
	"dojo/text!csi/templates/view.html",

	"./Pdf",
	"./Print",
	"./Refresh",
	"./ToolbarFind",
	"./ToolbarFullScreen",
	"./ToolbarPageSize",
	"./ToolbarPluginActions"
], function(array, declare, event, lang, aspect, dom, domAttr, domClass, domConstruct, all, topic, when, win, _TemplatedMixin, _Widget, _WidgetsInTemplateMixin, Button, registry,
	csi, csiDeferred, Tracking, _, access, csiAction, comm, constants, {parseExtendedPath}, Key, SpecBuilder, def, Context, csiHeading, Specs, html, locale, message, {convertEnumToComponentItem},
	csiPrefs, reflection, resolver, {findAttributeKey, findUntypedAttributeKey, getVisibleAttributeKeys},
	{hasAttributeDependency}, openTelemetryService, {generateAttributeKeysFromHeadings, generateSpecsFromAttributeKeys}, {registerHoldPreSaveCallback, requestWriter, submitHoldWriter},
	session, sessionConstants, state, {routerStore, sessionStore}, csiStore, Names, UserNames, csiView, csiViewUtil, _HighlightMixin, CheckboxDialogAction, Tooltip, xml, ViewTooltip, template) {

	/**
	 * @typedef {Object} ActionCheckboxConfig
	 * The configuration object for dialog action checkbox
	 * The format of the object is following:
	 * @property {string} id - id
	 * @property {number} pageIndex - target view page index
	 */

	/**
	 * @typedef {Object} EventHeadingContext
	 * Object holding contextual heading information for mouse click
	 *
	 * @property {Context} ctx - Context used to render Heading content
	 * @property {Heading} h - Heading used to render content in target cell
	 * @property {string|number} [keyContainer] - index into container attribute
	 * @property {DOMNode} refNode - node for which tooltip is generated for
	 * @property {Renderer} ren - Renderer class used to render Heading
	 */

	html.registerDataAttribute("DATA_RESULT", "result");
	html.registerDataAttribute("DATA_RESULT_ACTION", "result-action", Boolean);

	csiAction.registerControllers("", {
		CustomViewManage: "csi.view.CustomViewController",
		Switchboard: "csi.view.SwitchboardController",
		ViewFind: "csi.view.FindController",
		ViewFullScreen: "csi.view.FullScreenController",
		ViewPageSize: "csi/view/QuerySizeController",
		ViewPdf: "csi.view.PdfController",
		ViewPrint: "csi.view.PrintController",
		ViewQuery: "csi.view.QueryController",
		ViewQueryMore: "csi.view.QueryMoreController",
		ViewRefresh: "csi.view.RefreshController",
		ViewSelect: "csi.view.SelectController"
	});

	// publish topic indicating changes to DOM at slower pace so as not to cause callbacks to fire too often
	var _publishDomChange = _.debounce(function(domNode) {
		topic.publish("/csi/view/change", domNode);
	}, 50);

	return declare([_Widget, _TemplatedMixin, _WidgetsInTemplateMixin, _HighlightMixin], {
		templateString: template,

		// Actions
		ACTION_SELECT: "ViewSelect",

		// DOMNode: connection point for view actions
		//actionsNode: null,

		/** @property {boolean} allowChildToScroll - set to true if child view can scroll based on available height */
		//allowChildToScroll: false,

		/** @property {ICustomViewAttribute[]} attributeKeys - keys for attributes to display in view */
		//attributeKeys: null,

		/** @property {Specs} attributeSpecs - specs to render in property view */
		attributeSpecs: null,

		// Boolean: indicates if view is contained within dashboard (GridContainer)
		//bDashboard: false,

		// Boolean: indicates if disabling view should process actions in view content (only makes sense for leaf views that do not contain other views)
		//bDisableContentActions: false,

		/** @property {boolean} bFocused - indicates if view is in focused pane */
		//bFocused: false,

		// Boolean: set to true to force link to all nodes, false to disable links to all nodes
		//bForceBrowse: false,

		/** @property {boolean} bInSwitchboard - indicates if view is under a switchboard view */
		//bInSwitchboard: false,

		// Boolean: indicate if view is being shown in popup dialog
		//bPopup: false,

		// Boolean: if set to true then print view will query for own data rather than reuse the cache from the existing view
		//bPrintResetCache: false,

		// Boolean: if set to true then print CSS will be applied to home view as well
		//bPrintView: false,

		// Boolean: if set to true then view will resize on any call to publishDomChange
		//bResizeOnChange: false,

		// Boolean: indicates if view has been drawn at least once
		//bViewed: false,

		// Cache: cache for view
		//cacheView: null,

		/** @property {boolean} cannotReuseStoreForPrint - set to true if view's store cannot be reused when printing */
		//cannotReuseStoreForPrint: false,

		// String: root CSS for view
		//"class": "",

		/** @property {string} compositeCustomViewName - localized name for view in composite custom view */
		//compositeCustomViewName: null,

		/** @property {Heading[]} configuredHeadings - list of headings configured in view */
		configuredHeadings: null,

		// String: message to display if problem with view
		//contentMessage: null,

		/** @property {FunctionComponent} customViewOptionsComponent - the custom view rendering component */
		//customViewOptionsComponent: null,

		/** @property {CustomViewOptionsStore} customViewOptionsStore - the custom view data store */
		//customViewOptionsStore: null,

		// View: view definition for widget
		//defView: null,

		/** @property {ActionCheckboxConfig[]} dialogActionCheckboxes - the array of  configuration object for dialog action checkboxes */
		//dialogActionCheckboxes: null,

		// Boolean: set widget to disabled initially so that others can watch for view to get enabled first time
		disabled: true,

		/** @property {boolean} disableOtherViewsWhenModal - if this view is made a modal view, then indicate if other views should be disabled */
		//disableOtherViewsWhenModal: false,

		/** @property {boolean} disableTabMatching - indicates if links within view should navigate to first tab rather than last tab visited */
		//disableTabMatching: false,

		/** @property {boolean} flexHeight - set to true if table can just size vertically to container */
		//flexHeight: false,

		/** @property {boolean} flexHeightPropagetesToChildren - set to true if flexHeight value from this view should propagate to children */
		flexHeightPropagetesToChildren: true,

		// String: selected image size for view with value from ImageSize enum
		//imageSize: null,

		// InvalidMessage: widget displaying validation message for view
		//invalidNode: null,

		/** @property {boolean} isPreview - indicates if view is being shown as a preview which restricts various of the view's behaviors */
		//isPreview: false,

		// Error: indicates if view encountered error while loading
		//errorOnLoad: null,

		// DOMNode: connection point for container of any rendered headings in view
		//headingsNode: null,

		/** @property {LayoutItem} [layoutItem] - layout item that created view (if directly under a tab) */
		//layoutItem: null,

		/** @property {boolean} [omitValidActionCheck] - action controller checks if view is valid while processing; ignore such a check if this is true */
		//omitValidActionCheck: false,

		// Object: map of classes to use for toolbar plugins to override default ones
		//pluginClasses: null,

		// Any[]: toolbar plugins configuration
		//plugins: null,

		// String[]: ordered list of toolbar plugin ids that should go to the right of toolbar (first is rightmost)
		//pluginsRight: null,

		// String: custom view key
		//prefKey: null,

		// String: context for custom view key based on data in view (such as product or node type)
		//prefNodeContext: null,

		// String: context for custom view key based on how view is referenced (such as by an action or another view, i.e. Html, Wizard, etc.)
		//prefViewContext: null,

		/** @property {string} [prefViewContextOld] - view context that may have used in previous version to identify custom view context */
		//prefViewContextOld: null,

		/** @property {string} [prefViewContextUsed] - full custom view context that stores the custom view loaded into the view */
		//prefViewContextUsed: null,

		// Boolean: print setting indicating to render attribute comments
		//printComments: false,

		// Boolean: print setting indicating to render header logo on each page
		//printHeader: false,

		// String: paper size to use when printing
		//printPaper: "",

		// String: orientation to use when printing
		//printOrientation: "",

		// csi.view.Widget: cross-reference to source view when printing
		//printSourceView: null,

		/** @property {C8NodeType} productType - derived class of ProductType shown in this view */
		//productType: "",

		/** @property {boolean} refreshOnTabSwitch - refresh data in view when switching tabs make view visible again */
		//refreshOnTabSwitch: false,

		// Number: timestamp indicating when view was last refreshed
		//refreshTimestamp: 0,

		// ExtendedPath[]: provides the hook to add ExtendedPath - can be rendered when relative is set to true in custom view filter
		//relativePaths: null

		// Relationship: root relationship for view (defaults to defView.getPageRelationship)
		//relRoot: null,

		/** @property {DOMNode} scrollNode - element where view can scroll if too tall (necessary for view to scroll in popup dialogs) */
		//scrollNode: null,

		// String: optional string for view to help identify it uniquely
		//secondaryViewId: null,

		// Boolean: do we maintain separate views for Supplier vs Host users?
		//separateSupplierViews: false,

		/** @property {boolean} showFileSizes - if true then query for and render file sizes for any file attributes */
		showFileSizes: false,

		/** @property {SpecBuilder} specBuilder - object to build specs for view */
		//specBuilder: null,

		// String: type of splitter to use for splitting view content (composite, gallery, property, table)
		//splitterType: null,

		// String: class to use for toolbar widget
		toolbarClass: "csi/view/Toolbar",

		// Widget: class to use for tooltip widget
		tooltipClass: ViewTooltip,

		// Toolbar: toolbar widget for view
		//toolbarNode: null,

		// ViewTooltip: parent view to this one
		//tooltipView: null,

		// String: node url at root of view
		//urlRoot: null,

		/** @property {boolean} useMuiTooltipStyling - indicates if view should use MUI styling for tooltips (such as when contained within a React component) */
		useMuiTooltipStyling: false,

		// csi.view.Widget: inserted view to this one - normally created to render nested layout
		//viewLayout: null,

		// csi.view.Widget: parent view to this one
		//viewParent: null,

		/** @property {Writer} writerForActions - if specified, then use writer for any actions in the view */
		//writerForActions: null,

		// Object: cached results for access control checks
		//_accessCache: null,

		// Object: map of action controllers for view
		//_actionControllers: null,

		// Boolean: indicates if list of headings has changed - used to dynamically load heading renderers
		_bHeadingsDirty: true,

		// Boolean: indicates if view has already been resolved
		_bResolved: false,

		// Boolean: used temporarily to disable view rendering when refresh is called recursively
		//_bLoading: false,

		// Boolean: used in SubtypedDatasheet to skip resolving headings for subtypes that are not displayed
		// bSkipRender

		// Boolean: indicates if toolbar actions have already been added
		//_bToolbarActions: false,

		// Deferred[]: deferred object tracking view building
		//_deferreds: null,

		// Object: dialog action checkboxes for view
		//_dialogCheckboxes: null,

		/**
		 * @property {boolean} _enableToolbarAfterRedraw - set to true when toolbar needs to be enabled after redraw;
		 * the toolbar is normally enabled by the action controller when done for view that is subject of action, but
		 * if view is different from action then toolbar may need to be refreshed explicitly
		 */
		//_enableToolbarAfterRedraw: false,

		// Number: delay for resize timeout
		_resizeDelay: 100,

		// Object: timer handle used to resize view
		//_resizeTimeout: null,

		// Object: tracks attributes used in rollup expressions
		//_rollupDependencies: null,

		// Object: object mapping heading keys to relationship stores for editing
		//_headingStores: null,

		// Object: object mapping matrix ids to data stores used by them
		//_matrixStores: null,

		// String: id of switchboard plugin
		//_switchboard: null,

		// DOMNode: node for holding tooltips in view
		//_tooltipNode: null,

		// Span: OpenTelemetry span
		//_otelSpan: null,

		/** Function disableNewAction is used to conditionally disable (but keep visible) new actions in toolbar actions button
		 * Example: modules/SpecificationDataSheet/Core/SectionGrouping
		 * @param {Action} a
		 * @return {boolean} - return true to disable actions
		 * undefined disableNewAction function works the same way as "return false"
		 */
		//disableNewAction: function() { return false; }

		toString: function() {
			return this.defView ? this.defView.toString() : this.id;
		},

		/**
		 * Return true if view allows selection of nodes
		 * @return {boolean}
		 */
		allowsSelections() {
			const {defView, isPreview} = this;
			return !isPreview && !!defView.m_formData?.m_bAllowSelections;
		},

		/**
		 * Add view parameters to given store so that server requests indicate where they came from
		 * @param {RequestStore} store
		 */
		annotateViewStore(store) {
			store.addRequestParameter?.("UI_CustomView", this.customViewId);
			store.addRequestParameter?.("UI_View", this.defView.getKey());

			// if request already exists for store, then annotate it too
			// NOTE: some store requests are simple objects with a "post" function, so ignore those that are not full requests
			if (store.request?.addParameter) {
				this.annotateViewRequest(store.request);
			}
		},

		/**
		 * Add view parameters to given writer so that server requests indicate where they came from
		 * @param {Request} request
		 */
		annotateViewRequest(request) {
			request.addParameter?.("UI_CustomView", this.customViewId);
			request.addParameter?.("UI_View", this.defView.getKey());
		},

		/**
		 * Add view parameters to given writer so that server requests indicate where they came from
		 * @param {Writer} writer
		 */
		annotateViewWriter(writer) {
			this.annotateViewRequest(writer.m_request);
		},

		applyPrintCSS: function(/*String?*/paper, /*String?*/orientation, /*DOMNode?*/domNode) {
			// summary: remove previous and add new CSS to view based on print settings
			domNode = domNode || this.domNode;
			var printCss = this.getPrintCSS(paper, orientation);
			var css = html.getClass(domNode);
			if (css) {
				css = css.replace(/print\w+(Portrait|Landscape)/, "") + " " + printCss;
			}

			html.setClass(domNode, css || printCss);
		},

		/**
		 * Return true or false indicating if button should be be based on current conditions
		 * @param {Button} button
		 * @param {string} idAction
		 * @return {boolean}
		 */
		canEnableButton: function(button, idAction) {
			return true;
		},

		/**
		 * Return true if all parent views allow child to scroll automatically
		 * @return {boolean}
		 */
		canScrollAutomatically: function() {
			var view = this.viewParent;

			while (view && view.allowChildToScroll) {
				view = view.viewParent;
			}

			return !view;
		},

		/**
		 * Return true or false indicating if button should be displayed based on current conditions
		 * @param {string} idAction
		 * @return {boolean}
		 */
		canShowButton: function(idAction) {
			return true;
		},

		/**
		 * Return true if checkbox should be enabled based on current conditions
		 * @param {ActionCheckboxConfig} config
		 * @return {boolean}
		 */
		canEnableActionCheckbox: function(config) {
			return true;
		},

		/**
		 * Return true if checkbox should be displayed based on current conditions
		 * @param {ActionCheckboxConfig} config
		 * @return {boolean}
		 */
		canShowActionCheckbox: function(config) {
			return true;
		},

		/**
		 * Create view definition to be used to print current view
		 * @return {View}
		 */
		createViewDefinition() {
			const {defView: {m_id, m_idType}} = this;
			return def.typeDefinition(m_idType).getViewDef(m_id);
		},

		getCache: function() {
			return this.cacheView;
		},

		getAction: function(/*String*/idAction, /*DOMNode?*/element, /*Object?*/params) {
			// summary: find action for given id in view
			const {defView, headingsNode, tooltipView, writerForActions} = this;
			var ctx = this.getContext();
			var action;
			var h;
			var refNode;
			var ren;

			// first look for actions in tooltip - tooltip actions could include view actions, so do this before looking for actions on view
			if (element && tooltipView?.defActions) {
				action = tooltipView.defActions.getAction(idAction);
				refNode = tooltipView.refNode;

				if (action && refNode) {
					// if action uses a path, then tooltip action is operating in a different context than that of attribute in cell;
					// current example is editing Tags on referenced Image (see csi/heading/RefImage)
					var elementWithContext = action.hasOwnContext() ? element : refNode;
					ctx.readNodeContext(elementWithContext);
					ctx.keyValue = csiHeading.getHeadingValueKey(elementWithContext);

					h = action.heading;
					ren = h ? csiHeading.getHeadingRendererInContext(ctx, h) : null;
				}
			}

			// next, look for action on view
			if (!action) {
				action = defView.getAction(idAction);
				refNode = null;
			}

			// extract heading information
			if (element && headingsNode && (!action || !refNode)) {
				refNode = csiHeading.findParentHeadingNode(element, headingsNode);

				if (refNode && !csiHeading.isEditing(refNode)) {
					ctx.readNodeContext(refNode);

					const headingActionId = csiHeading.getHeadingNodeMarker(refNode) || idAction;
					const otherActionId = (headingActionId !== idAction) ? idAction : null;
					let headingElement = element;

					// find heading from attributes in view
					h = this.getHeading(headingActionId, refNode);

					if (h) {
						if (otherActionId) {
							// it's possible a subheading is also editable
							const sh = h.m_subheadings?.find((sh) => sh.getKeyDP() === otherActionId);
							if (sh) {
								h = sh;
							}
						} else {
							// if not a subheading, then get information from root heading element
							headingElement = refNode;
						}
					}

					// this is needed to handle special cases where value was rendered using custom heading not found in (custom) view
					h ||= this.getHeading(idAction, refNode);

					if (h) {
						ctx.keyValue = csiHeading.getHeadingValueKey(headingElement);
						ren = csiHeading.getHeadingRendererInContext(ctx, h);

						if (!action) {
							if (ren) {
								action = ren.getAction(ctx, h);
							} else if (h.m_subheadings && (refNode != element)) {
								// it is possible sub-heading is not editable and has path that will change context so
								// that renderer cannot be found; so in that case try getting action using refNode
								params.m_element = refNode;
								return this.getAction(idAction, refNode, params);
							} else {
								console.warn("csi.view.getAction: no renderer for heading: " + idAction);
							}
						}
					}
				} else if (!action) {
					// cannot find any action for given element, or disallow while still editing
					return false;
				}
			}

			// add heading controller specific parameters
			if (params && action) {
				params._ctx = ctx;
				params.heading = h;
				params.keyContainer = ctx.keyValue;
				params.ren = ren;
				params.refNode = refNode;

				// assign writer to controller parameters if view so configured
				if (writerForActions) {
					writerForActions.cache = ctx.cache;
					params.m_writer = writerForActions;
				}
			}

			return action;
		},

		getActionButton: function(/*String*/idAction) {
			// summary: return button with given action id
			return html.queryDom([html.getDataAttributeSelector(html.DATA_ACTION, idAction)], this.actionsNode, true);
		},

		/**
		 * Return action node container
		 * @return {domNode}
		 */
		getActionContainer: function() {
			return this.domNode;
		},

		getActionController: function(/*Context*/ctx, /*Action*/action) {
			// summary: return special action controller based on context
			return this._actionControllers ? this._actionControllers[action.m_id] : null;
		},

		getActionControllerContexts: function(/*String*/idAction) {
			// summary: by default, returns types for configured view
			return this.defView.getTypes();
		},

		/**
		 * Return the default dropdown options
		 * @return {ICustomViewDropDownOptions}
		 */
		getCustomViewDropDownOptions() {
			// Paper Size dropdown options
			const paperSizes = convertEnumToComponentItem("PaperSize", true);
			paperSizes.unshift({id: constants.PREFERENCE_DEFAULT, res: "Default"});

			// Paper Orientation dropdown options
			const paperOrientations = convertEnumToComponentItem("PaperOrientation", true);
			paperOrientations.unshift({id: constants.PREFERENCE_DEFAULT, res: "Default"});

			return {
				paperOrientations,
				paperSizes,
			};
		},

		/**
		* Return the default image size
		* @return {string} -  the image size
		*/
		getDefaultImageSize: function() {
			return this.defView.defaultImageSize;
		},

		/**
		 * Return image size to use in view
		 * @return {string}
		 */
		getEffectiveImageSize() {
			return this.imageSize;
		},

		/**
		 * Return Heading for given key
		 * @param {C8AttributeKey | Key} key 
		 * @param {HTMLElement} [domNode] - optional element where heading was rendered; only Table views have different specs/headings per level
		 * @return {Heading | undefined}
		 */
		getHeading(key, domNode) {
			return this.getHeadingSpec(key)?.heading;
		},

		/**
		 * Return Spec for given key
		 * @param {C8AttributeKey | Key} key
		 * @param {boolean} ignoreMatrixGroup
		 * @return {Spec | undefined}
		 */
		getHeadingSpec(key, ignoreMatrixGroup) {
			return this.attributeSpecs?.getByKey(key, false, false, ignoreMatrixGroup) || this.specBuilder.getCachedSpec(key, true, ignoreMatrixGroup);
		},

		getPrintCSS: function(/*String?*/paper, /*String?*/orientation) {
			// summary: return CSS used for printing based on current paper size and orientation
			return "print" + (paper || this.getPrintPaper()) + (orientation || this.getPrintOrientation());
		},

		getPrintOrientation: function() {
			// summary: return orientation to use for printing view
			return this.printOrientation || sessionConstants.sitePaperOrientation;
		},

		getPrintPaper: function() {
			// summary: return paper to use for printing view
			return this.printPaper || sessionConstants.sitePaperSize;
		},

		getSourceController: function() {
			// summary: if popup view, return action controller which opened this view
			var popup = this.getPopupView();
			return popup ? popup.defView.getSource() : null;
		},

		cachedAccessToAction: function(/*Context*/ctx, /*Action*/action) {
			// a small optimization for performance:
			// not to check on every cell for access to the view
			// check once and then store in the view object access to this view per node and resource
			var node = ctx.getContextNode();
			var resourceID = action.getKey();

			if (node && node.$URL && resourceID) {
				var key = node.$URL + "," + resourceID;
				var obj = this._accessCache = this._accessCache || {};
				obj[key] = obj[key] || this.getAccessToAction(ctx, action);
				return obj[key];
			}

			return this.getAccessToAction(ctx, action);
		},

		collectRequestsForTesting: function(/*Node*/rootNode, /*Object*/rootQuery, /*Request[]*/requests, /*Boolean*/includeAllRequests) {
			// summary: return requests in view that will be used for performance testing
			const store = this.get("store");
			const options = this.getQueryResultOptions?.();
			let req;

			if (includeAllRequests) {
				if (req = this._getRootRequest(rootNode)) {
					requests.push(req);
				}
			}

			if (req = store?.getRequest?.(rootQuery, options)) {
				requests.push(req);
			}
		},

		destroyView: function() {
			// summary: same as destroyRecursive, except protect against destroying something that was never created
			// FIX: do we really need this now that we are using proper lifecycle?
			if (this.domNode) {
				this.destroyRecursive();
			} else {
				this.uninitialize();
			}
		},

		doesQueryTotalExceedMax: function() {
			// summary: by default there is no maximum
			return false;
		},

		/**
		 * Force view to be read-only
		 */
		forceReadOnly() {
			this.defView.setReadOnly(true);
			this._disableView(false);
		},

		getAccessToAction: function(/*Context*/ctx, /*Action*/action) {
			// summary: determine if view has access to given action key for given node
			return access.getViewResourceAccess(this.defView.getTypeNode(), this.defView.getId(), action.getKey(), ctx.getContextNode());
		},

		hasContent: function() {
			// summary: return true if view is showing content to print/export
			return true;
		},

		/**
		 * Return true if refNode is NewAction button
		 * @param {DOMNode} refNode
		 * @return {boolean}
		 */
		isNewActionsButton: function(refNode) {
			return !!(refNode && html.getDataAttribute(refNode, "ButtonNewActions"));
		},

		isChildViewOf: function(/*csi.view.Widget*/parentView) {
			// summary: return true if this view is the same as, or a descendant view of, the given view
			var v = this;

			while (v && (v != parentView)) {
				v = v.viewParent;
			}

			return !!v;
		},

		isLoadingView: function() {
			// summary: if deferred object exist then view is loading
			return !!this.getDeferred();
		},

		isStarted: function() {
			// summary: return if startup has been called
			return this._started;
		},

		isShowingRootNode: function() {
			// summary: return true if view is querying and using root node
			return this.defView.shouldResolveRootClass();
		},

		isValidView: function(/*Boolean*/bEnd) {
			// summary: validate that view is still part of DOM, and if not, go to end if so instructed
			if (html.exists(this) && !this._bLoading) {
				return true;
			}

			if (bEnd) {
				this._endView();
			}

			return false;
		},

		loadViewClasses: function() {
			// summary: load classes necessary for view to render - return view for easier callback processing
			var dfds = [];

			if (this._bHeadingsDirty) {
				this._bHeadingsDirty = false;

				// if any headings in view reference node properties CR or PB, then need to query for user names to render properly
				if (this.defView.findHeadingBy(function(h) {return (h.m_prop == xml.PROP_CREATOR) || (h.m_prop == xml.PROP_PUBLISHED_BY);})) {
					dfds.push(UserNames.load());
				}
			}

			return all(dfds);
		},

		setViewNode: function(/*Node*/node, /*Boolean*/bSaveSelections) {
			// summary: update view and toolbar context

			// refresh selected nodes before view is refreshed - don't need to pass context
			// to undo selections since view will be refreshed anyways
			if (this.defView.m_formData && !bSaveSelections) {
				this.defView.m_formData.resetSelections();
			}

			if (node) {
				this.set("urlRoot", node.$URL);
				this.cacheView.cacheNode(node);
			} else {
				this.set("urlRoot", null);
			}

			if (this.domNode) {
				html.setElementContext(this.domNode, this.urlRoot);
			}

			// signal change to view data
			if (this.bViewed) {
				this.onChangeData();
				this._resetStores();

				// if this view has already been viewed, then disabled plugins until view is redrawn
				if (this.toolbarNode) {
					this.toolbarNode.disablePlugins();
					this._enableToolbarAfterRedraw = true;
				}
			}
		},

		/**
		 * Return placeholder actions after filtering through getNewActions
		 * @return {Action[]}
		 */
		getFilteredPlaceholderActions() {
			const qrRoot = this.getQueryResult();
			const ctx = qrRoot.getChildContext();
			const viewPlaceholderActions = this.placeholderActions;
			let filteredPlaceholderActions = [];

			if (viewPlaceholderActions?.length) {
				const menuActions = this.defView?.defToolbar?.getMenuActions();
				const placeholderActions = menuActions?.filter((a) => viewPlaceholderActions.includes(a.getId()));

				if (placeholderActions?.length) {
					filteredPlaceholderActions = qrRoot.getNewActions(ctx, false, null, null, placeholderActions);
				}
			}

			return filteredPlaceholderActions;
		},

		getViewNode: function() {
			// summary: return node owning view
			return this.cacheView.getNode(this.urlRoot);
		},

		getViewNodeType: function() {
			// summary: return root node type for view; if root node does not exist, then use class information
			var node = this.getViewNode();
			if (node) {
				return node.$Type;
			}

			var rc = this.defView.getRootClass();
			return rc && rc.getType();
		},

		getViewURL: function() {
			// summary: return url of node owning view
			return this.urlRoot;
		},

		/**
		 * Return true if view supports custom views
		 * @return {boolean}
		 */
		supportsCustomViews: function() {
			return false;
		},

		/**
		 * Return properties that should be applied to the custom view preview
		 * @return {Record<string, any>}
		 */
		getPreviewProperties() {
			return {};
		},

		/**
		 * Return properties that should be applied to the print preview
		 * @return {Record<string, any>}
		 */
		getPrintProperties() {
			return {};
		},

		getViewPreferenceKey: function() {
			return this.prefKey || this.defView.getKey();
		},

		getViewPreferenceContext: function(/*Boolean?*/bOmitViewContext, /*Boolean?*/bOmitNodeContext) {
			// summary: get context identifier for custom views
			var viewContext = this.prefViewContext;
			if (viewContext == null) {
				var fd = this.defView.m_formData;
				if (fd) {
					// DE10833 - prefer to use original source for context since that is the original action context;
					// this makes sense since that is the true context from where the action arose rather than the context
					// the controller might use to get the desired functionality
					// FIX: eventually set prefViewContext above when setting form data rather than calculate dynamically
					viewContext = fd.getViewPreferenceContext();
				}
			}

			return csiPrefs.composeKey(this.getViewPreferenceKey(), bOmitViewContext ? null : viewContext, bOmitNodeContext ? null : this.prefNodeContext);
		},

		/** 
		 * Return configurable query levels as an array - only views using _QueryMixin will have them
		 * @return {QueryLevel[] | undefined}
		*/
		getConfigurableQueryLevels() { },

		getContext: function(/*String?*/urlNode, /*String?*/urlParent, /*String?*/idRelation) {
			// summary: return Context for view - if no urls given, then use default for view
			if (!urlNode && !urlParent) {
				urlNode = this.urlRoot;

				// if relationship, then make root node of view as the parent
				if (this.relRoot) {
					idRelation = this.relRoot.getAttributeId();
					urlParent = urlNode;
					urlNode = null;
				}
			}

			var ctx = new Context(this, this.cacheView, urlNode, urlParent, idRelation);

			// extension point to allow plugins to affect current context
			this.onContext(ctx);

			return ctx;
		},

		getSourceContext: function() {
			// summary: return view context of source view, such as when in popup
			var source = this.defView.getSource();
			var sourceView = source && source.widget;
			return sourceView && sourceView.getContext(sourceView.getViewURL());
		},

		getChildView: function(/*String*/idView) {
			// summary: find child view with given id
			var child;

			this.traverseViews(function(v) {
				if (v.defView.m_id == idView) {
					child = v;
				}
			});

			return child;
		},

		getSiblingView: function(/*String*/idView) {
			// summary: return view that is sibling of current view
			const parentView = this.viewParent;
			let siblingView;

			if (parentView) {
				siblingView = parentView.getChildView(idView);
			} else {
				const matchingViews = csiView.getAllViews(idView);
				if (matchingViews) {
					siblingView = matchingViews.find((view) => !view.viewParent);
				}
			}

			return (this !== siblingView) ? siblingView : null;
		},

		/**
		 * Return array of action buttons for given view.
		 * @param {boolean} [bActive] - if true, then make sure button is shown and enabled
		 * @return {_WidgetBase[]}
		 */
		getActionButtons: function(bActive) {
			var buttons = this.actionsNode ? html.getChildWidgets(this.actionsNode) : [];

			if (bActive) {
				buttons = _.filter(buttons, function(w) {
					return !html.isDisabled(w) && html.isVisible(w);
				});
			}

			// filter out any checkboxes
			buttons = _.filter(buttons, function(w) {
				return (w.type === "button") || (w.type == "submit");
			});

			return buttons;
		},

		getForm: function() {
			// summary: find form containing widget - if any
			return html.getParentByTagName(this.domNode, "form");
		},

		getPlugin: function(/*String*/s) {
			return this.toolbarNode ? this.toolbarNode.getPlugin(s) : null;
		},

		getPopupView: function() {
			// summary: return view at root of popup dialog if view is in popup
			if (!this.bPopup) {
				return null;
			}

			var view = this;

			while (view.viewParent && view.viewParent.bPopup) {
				view = view.viewParent;
			}

			return view;
		},

		getToolbarContainerView: function(/*Boolean?*/bOmitSelf) {
			// summary: traverse parent view until find one with own toolbar
			var v = bOmitSelf ? this.viewParent : this;

			while (v && !v.bPrintView) {
				v = v.viewParent;
			}

			return v;
		},

		getTooltipNode: function() {
			// summary: return tooltip container for view
			if (!this._tooltipNode) {
				this._tooltipNode = domConstruct.create("div", {"class": "csiViewTooltips"}, this.domNode);
			}

			return this._tooltipNode;
		},

		hasPlugin: function(/*String*/s) {
			// summary: check if view has given plugin - it is possible this is called before the toolbar is build, so if it doesn't
			// exist on toolbar then check plugins array
			// NOTE: this currently does not support plugins in menus if not found on toolbar
			return this.getPlugin(s) || _.contains(this.plugins, s);
		},

		// Begin RefNode/Selection API - should be Mixin for views handling multiple result nodes
		// such as table and gallery views (maybe also include form data?)

		enableRefNodeHighlighting: function(/*DOMNode*/refNode) {
			// summary: handle highlighting and selection of given row
			var fd = this.defView.m_formData;
			var url = this.getRefNodeUrl(refNode);

			// add events to highlight node
			if (!state.get("print")) {
				this.enableHighlighting(refNode);

				if (!this.enableMassSelect) {
					html.setElementAction(refNode, this.ACTION_SELECT);
				}
			}

			// add select styling to node if already selected
			if (fd && fd.isSelected(url)) {
				this.onSelect(this.getContext(), url, true, false);
			}
		},

		enableRefNodeSelection: function(/*DOMNode*/rootNode) {
			// summary: register action to select ref nodes - does not make sense for print view
			if (!state.get("print")) {
				// keep view enabled when selecting so quick selections in large table do not slow down due to enabling/disabling all checkboxes
				this.defView._addViewAction(this.ACTION_SELECT, {
					bKeepViewEnabled: true
				});
			}
		},

		getQueryResult: function(/*String?*/id, /*Boolean?*/bExists) {
			// summary: QueryResult only used on views displaying multiple results; return null otherwise
			return null;
		},

		/**
		 * Return QueryResult for given DOMNode; if domNode is null, then root QueryResult will be returned
		 * @param {DOMNode} domNode
		 * @return {QueryResult}
		 */
		getQueryResultByDomNode: function(domNode) {
			var refNode = this.getRefNode(domNode);
			var id = refNode ? this.getRefNodeId(refNode) : null;
			return this.getQueryResult(id);
		},

		getRefNode: function(/*DOMNode*/domNode) {
			// summary: return DOMNode associated with url to support selection
			return html.getNodeWithDataAttribute(domNode, html.DATA_RESULT, this.domNode);
		},

		getRefNodeById: function(/*String*/id) {
			// summary: return DOMNode for given QueryResult id
			return null;
		},

		getRefNodeByUrl: function(/*String*/url) {
			// summary: return DOMNode for given result url
			return null;
		},

		getRefNodeId: function(/*DOMNode*/refNode) {
			// summary: return QueryResult id for given node
			return html.getDataAttribute(refNode, html.DATA_RESULT);
		},

		getRefNodeNext: function(/*DOMNode*/refNode, /*Boolean*/bPrevious, /*Boolean*/bTestRefNode, /*Function?*/fTest) {
			// summary: find next refNode in view that matches given tests
			if (!refNode) return null;

			var r = bPrevious ? refNode.previousSibling : refNode.nextSibling;

			while (r) {
				if (!bTestRefNode || this.isRefNode(r, true)) {
					if (!fTest || fTest(r)) {
						return r;
					}
				}

				r = bPrevious ? r.previousSibling : r.nextSibling;
			}

			return null;
		},

		/**
		 * Return dependency paths necessary to display the result node represented by the given refNode;
		 * if a starting path is given, then only return dependency paths that extend beyond that path
		 * @param {DOMNode} [refNode]
		 * @param {string} [startingPath]
		 * @return {string[]}
		 */
		getRefNodePaths: function(refNode, startingPath) {
			var qr = this.getQueryResultByDomNode(refNode);
			var paths;

			if (qr) {
				paths = qr.level.getDependencyPaths();
			} else {
				paths = def.getHeadingsDependencyPaths(this.defView.getHeadings());
			}

			if (startingPath) {
				const basePath = parseExtendedPath(startingPath, null, true);
				const subpaths = [];

				paths.forEach((p) => {
					const ep = parseExtendedPath(p, null, true);

					if (ep.isAlongPath(basePath, true)) {
						const {standardPath} = ep.relativeTo(basePath);
						if (standardPath) {
							subpaths.push(standardPath);
						}
					}
				});

				paths = subpaths;
			}

			return paths;
		},

		getRefNodes: function() {
			// summary: get all refNodes in given container of entire view
			return [];
		},

		getRefNodeUrl: function(/*DOMNode*/refNode) {
			// summary: return url associated with DOMNode
			return html.getElementContext(refNode, true);
		},

		/**
		 * Return result node used to render given DOM element
		 * @param {HTMLElement} domNode 
		 * @return {C8Node | undefined}
		 */
		getResultNodeByDomNode(domNode) {
			const {cacheView, urlRoot} = this;
			const refNode = this.getRefNode(domNode);
			const url = this.getRefNodeUrl(refNode) || urlRoot;
			return cacheView.get(url);
		},

		/**
		 * Return true if whole refNode needs to be refreshed based on change to given heading
		 * @param {Heading} heading
		 * @param {Node} node
		 * @param {string} typeAttr
		 * @return {boolean}
		 */
		hasRefNodeDependency: function(node, heading, typeAttr) {
			// by default only determine if given attribute was used in an expression - path is currently ignored
			var id = heading.m_id;

			if (this._rollupDependencies && this._rollupDependencies[id]) {
				return true;
			}

			// determine if change to attribute has dependencies which require refreshing more than just attribute
			var defView = this.defView;
			var path = heading.getClassPath();
			var key = def.getHeadingKey(heading.getClassPath(), id, null, null, heading.m_index);
			var pathRef = null;

			var checkRule = function(rule) {
				return rule.hasAttributeDependency ? rule.hasAttributeDependency(id) : false;
			};
			var checkRules = function(obj) {
				return obj.m_rules && _.some(obj.m_rules, checkRule);
			};
			var checkRelationship = function(rel) {
				var actions = rel.m_defActions ? rel.m_defActions.getActions() : null;
				return checkRules(rel) || (actions && _.some(actions, checkRules));
			};
			var checkImage = function(h) {
				// if in property view and editing any image, then need to redraw node/view
				if (def.getHeadingKey(heading.m_path, id) == def.getHeadingKey(h.m_path, h.m_id)) {
					return true;
				}

				return checkHeading(h);
			};
			var checkHeading = function(h) {
				// we are only worried here about other headings relying on this one
				if (h == heading) return false;

				// return true if exact same attribute is shown but with multiple renderers
				var p = h.getClassPath();

				if (key == def.getHeadingKey(p, h.m_id, null, null, h.m_index)) {
					return true;
				}

				if (p) {
					// if changed a ref attribute, then check to see if another heading uses resulting path
					if (pathRef && (p.indexOf(pathRef) == 0)) {
						return ((p.length == pathRef.length) || (p.charAt(pathRef.length) == '/'));
					}

					// if paths do not match, then no need to refresh because of this heading
					if (path && (path != p) && ((path.indexOf(p) != 0) || (path.charAt(p.length) != '/'))) {
						return false;
					}
				}

				// check if heading has value expression that depends on id
				if (hasAttributeDependency(h.m_value, id)) {
					return true;
				}

				// now check heading and sub-heading rules for references to attribute
				return checkRules(h) ||
					(h.m_action && checkRules(h.m_action)) ||
					(h.m_subheadings && _.some(h.m_subheadings, checkRules));
			};

			if (xml.attributeTypeIsRef(typeAttr)) {
				pathRef = xml.pathAddStep(path, xml.PATH_OP_CHILD, id);
			}

			// test for any headings or relationships that rely on attribute change
			return _.some(defView.getHeadings(), checkHeading) ||
				_.some(defView.getImages(), checkImage) ||
				_.some(defView.getRelationships(), checkRelationship);
		},

		isRefNode: function(/*DOMNode*/domNode, /*Boolean*/bSelection) {
			// summary: determine if element supporting selecting
			return html.getDataAttribute(domNode, html.DATA_RESULT) && (!bSelection || !html.getDataAttribute(domNode, html.DATA_RESULT_ACTION));
		},

		/**
		 * Connection point to do something after plugins get enabled
		 */
		onEnablePlugins: function() { },

		onSelect: function(/*Context*/ctx, /*String*/url, /*Boolean*/bOn, /*Boolean*/bReplaced) {
			// summary: extension point for view selections; bReplaced is true if there was previously a selection (if bOn)
			// or will be a new selection (if not bOn)
			var refNode = this.getRefNodeByUrl(url);
			if (refNode) {
				this.select(refNode, bOn);
			}
		},

		/**
		 * Enable toolbar if was previously disabled
		 */
		reenableToolbar() {
			const {
				_enableToolbarAfterRedraw,
				disabled,
				toolbarNode,
			} = this;

			if (_enableToolbarAfterRedraw && toolbarNode && !disabled) {
				toolbarNode.enablePlugins();
				this._enableToolbarAfterRedraw = false;
			}
		},

		setRefNodeId: function(/*DOMNode*/refNode, /*String*/id) {
			// summary: mark element as refNode and optionally allow highlighting on mouse over
			html.setDataAttribute(refNode, html.DATA_RESULT, id);
		},

		toggleRefNodeSelection: function(/*DOMNode*/refNode, /*Boolean*/bSelect) {
			// summary: toggle selection of given item
			var url = this.getRefNodeUrl(refNode);
			var ctx = this.getContext(url);
			this.defView.m_formData.changeSelection(ctx, bSelect);
		},

		// End RefNode/Selection API

		scrollIntoView: function(/*DOMNode*/refNode) {
			// summary: by default use window api to scroll node into view
			if (refNode) {
				win.scrollIntoView(refNode);
			}
		},

		/**
		 * Resize view and all child views - when printing, no need to throttle requests as only resize requests come from
		 * explicit calls rather than user input (no scrollable tables, no drag&drop, resizing browser does not affect paper size)
		 */
		resize() {
			// ignore visibility check here as some views mark themselves invisible until resize is performed;
			// they can use display: none if they want to be fully hidden and not resized
			if (!this.isValidView() ||
				this.isLoadingView() ||
				!this.bViewed ||
				!html.isShown(this.domNode, true) ||
				state.get("printDone")
			) {
				return;
			}

			if (state.get("print") || state.get("forceResize")) {
				// if printing view, then resize immediately
				this._resizeView();
			} else {
				if (this._resizeTimeout) {
					clearTimeout(this._resizeTimeout);
				}

				this._resizeTimeout = setTimeout(() => {
					this._resizeTimeout = null;

					if (this.isValidView() && !state.get("printDone")) {
						if (this.isLoadingView()) {
							// if in the middle of loading, then delay resize until after loading is done
							this.when(this.getDeferred(), () => this.resize());
						} else {
							// resize now
							this._resizeView();
						}
					}
				}, this._resizeDelay);
			}

			this.inherited(arguments);

			if (this.viewLayout) {
				this.viewLayout.resize();
			}
		},

		traverseViews: function(/*Function*/f, /*Object?*/scope) {
			// summary: apply function to all inserted view to this one - keep traversing if function returns true
			var views = array.filter(csiView.getViews(), function(v) {
				return v.viewParent == this;
			}, this);

			scope = scope || this;

			array.forEach(views, function(v, i, arr) {
				if (f.call(scope, v, i, arr)) {
					v.traverseViews(f, scope);
				}
			});
		},

		/**
		 * Return true if view is in popup dialog or focused pane
		 * @return {boolean}
		 */
		isIsolatedView: function() {
			return this.bPopup || this.bFocused;
		},

		/**
		 * Return true if given typed key is in view
		 * @param {Key | C8AttributeKey} searchKey - typed key
		 * @param {boolean} [omitGroups] - if true, then no need to look into groups - just look at top-level keys
		 * @return {boolean}
		 */
		isKeyInView(searchKey, omitGroups) {
			const {attributeKeys, hiddenKeys} = this;

			if (findAttributeKey(hiddenKeys, searchKey, true)) {
				return false;
			}

			return !!findAttributeKey(attributeKeys, searchKey, omitGroups);
		},

		/**
		 * Return true if given untyped key is in view
		 * @param {Key | C8AttributeKey} searchKey - untyped key
		 * @param {boolean} [omitGroups] - if true, then no need to look into groups - just look at top-level keys
		 * @return {boolean}
		 */
		isUntypedKeyInView(searchKey, omitGroups) {
			const {attributeKeys, hiddenKeys} = this;

			if (findUntypedAttributeKey(hiddenKeys, searchKey, true)) {
				return false;
			}

			return !!findUntypedAttributeKey(attributeKeys, searchKey, omitGroups);
		},

		/**
		* Return true if view contains multiple child views
		* @return {boolean}
		*/
		isStackedView: function() {
			return false;
		},

		_getFormDataWidget: function() {
			// summary: return widget holding form data to validate
			return this;
		},

		errorForm: function(/*String*/errMessage, /*String?*/errCode) {
			// summary: notify widget of error in form - return true if view handles error, otherwise
			// return false to popup error dialog
			if (this.invalidNode) {
				this.invalidNode.set("maxWidth", this.domNode.offsetWidth);
				this.invalidNode.set("value", errMessage);
				this.publishDomChange();
				return true;
			}

			return false;
		},

		resetForm: function() {
			// summary: reset form data and invalid message
			if (this.invalidNode) {
				this.invalidNode.set("value", "");
			}
		},

		validateForm: function(/*Object*/errorParams) {
			// summary: validate view contents
			return true;
		},

		canAccessSelections: function(/*Action*/action) {
			// summary: check action access for all view selections
			var fd = this.defView.m_formData;
			if (!fd) return false;

			var nodes = fd.getSelectedNodes(true);
			if (!nodes.length) return false;

			var ctx = this.getContext();
			return array.every(nodes, function(node) {return action.hasAccess(ctx.setNode(node));});
		},

		preAction: function(/*Controller*/ac) {
			// summary: disable toolbar actions while performing action - if toolbar is held, that means it is already
			// disabled from previous action
			if (!this.get("disabled") && !ac.m_action.bKeepViewEnabled) {
				this.viewParent?.set("disabled", true);
				this.set("disabled", true);
			}
		},

		postAction: function(/*Controller*/ac, /*Boolean*/bKeepDisabled) {
			// summary: enable toolbar actions after actions if view is still valid
			if (!this.isValidView()) return false;

			// if action performed that affected data, then reset filter stores
			if (ac && ac.bDataChanged) {
				this.onChangeData();
			}

			// another action is pending, so don't bother to enable and then immediately disable toolbar
			if (!bKeepDisabled) {
				this.viewParent?.set("disabled", false);
				this.set("disabled", false);
			}

			return true;
		},

		publishDomChange: function(/*DOMNode?*/domNode) {
			// summary: notify others of change to DOM - ignore if change occurred in tooltip
			if (this.isValidView()) {
				if (!domNode || !html.getParentByClassName(domNode, "csiViewTooltips")) {
					_publishDomChange(domNode || this.domNode);
				}
			}
		},

		onChangeData: function() {
			// summary: extension point to react to change in any data in view
		},

		/**
		 * Handle click events for view
		 * @param {Event} e
		 * @return {string} action id invoked, if any
		 */
		onClick(e) {
			const {ACTION_SELECT, toolbarNode, tooltipView} = this;
			const {target} = e;

			// do not process clicks if inline editing is in progress
			// this will not prevent editor's "onBlur" method but will prevent abortion of current editing action
			// and therefore allow normal editor's onBlur behavior (validation or save or whatever else)
			if (csiHeading.isEditing(this.domNode)) {
				html.stopPropagation(e);
				return;
			}

			if (target) {
				// do not process clicks on the toolbar
				var tb = toolbarNode;
				if (tb && dom.isDescendant(target, tb.domNode)) {
					html.stopPropagation(e);
					return;
				}

				// do not process anchors with non-empty hrefs, but allow them to do default behavior
				var href = domAttr.get(target, "href");
				if (href && (href.indexOf(":void") < 0)) {
					html.stopPropagation(e);
					return;
				}

				// stop handling of the click event if the target is disabled.
				// It's needed for IE only, but doesn't hurt other browsers either
				if (domAttr.get(target, "disabled")) {
					event.stop(e);
					return;
				}
			}

			try {
				// Table/widget/onClick call inhertied (this function) and analyze returned value
				// Returned value will be used to detect action Select. Second click with action select will be toggling selected state
				// To detect action Select code in Table/widget/onClick will compare returned value to action select ID: actionInvoked == ACTION_SELECT
				const actionId = csiAction.invoke(this, target, null, null, e);

				// if no action under click, or click is to select an item, then also open tooltip if any (and not already open)
				if (tooltipView && (!actionId || actionId === ACTION_SELECT)) {
					const params = this.getEventHeadingContext(e);
					if (params) {
						const {ctx, h, refNode, ren} = params;
						tooltipView.forceOpen(refNode, {ctx, h, ren});
					}
				} else if (actionId) {
					Tooltip.hide();
				}

				return actionId;
			} catch (ex) {
				console.warn(ex);
			}
		},

		onContext: function(/*Context*/ctx) {
			// summary: extension point to affect view context
		},

		onDoubleClick(/*Event*/e) {
			// summary: invoke double-click event from containing heading renderer
			const params = this.getEventHeadingContext(e);
			if (!params) {
				return;
			}

			const {ctx, h, keyContainer, refNode, ren} = params;
			const action = ren.getDoubleClickAction(ctx, h);
			if (!action) {
				return;
			}

			try {
				csiAction.invoke(this, refNode, action, {
					heading: h,
					keyContainer,
					ren,
					refNode,
				});
			} catch (ex) {
				console.warn(ex);
			}

			event.stop(e);
		},

		onInsertViewParams: function(/*View*/defView, /*Node*/node, /*Object*/params) {
			// summary: extension point to allow parent view to influence child view
		},

		onKey: function(/*Event*/e, /*Object*/params) {
			// summary: handle keypress event in view
			return false;
		},

		/**
		 * Handle key release event in view
		 * @param {Event} e
		 * @return {boolean}
		 */
		onKeyRelease: function(e) {
			return false;
		},

		/**
		 * Respond to view's tab being selected, called from Tab/Widget->selectTab()
		 * can be used for manipulation with url parameters if view actively use them
		 * @param {LocationItemState[]} [locationState]
		 */
		onTabChanging: function(locationState) { },

		/**
		 * When view is shown due to switching tabs, then re-enable toolbar plugins based on latest data
		 */
		onTabSwitch: function() {
			const {refreshOnTabSwitch, toolbarNode} = this;

			this.refreshOnTabSwitch = false;

			if (!this.isLoadingView()) {
				if (toolbarNode) {
					toolbarNode.enablePlugins();
				}

				if (refreshOnTabSwitch) {
					this.refreshWidget();
				}
			}
		},

		/**
		 * Extension point called when view's tab is no longer selected
		 */
		onTabSwitching() { },

		redraw: function() {
			// summary: redraw view with current data stores
			return this._load(this._redraw);
		},

		redrawData: function() {
			// summary: redraw view with current data stores
			return this._load(this._redrawData);
		},

		/**
		 * Redraw view with updated images
		 * @param {cnl[]} imageUrls
		 * @return {Deferred}
		 */
		redrawImages(imageUrls) {
			if (!imageUrls.length) {
				return;
			}

			const refNodes = new Set();

			imageUrls.forEach((url) => {
				html.queryDom([html.getDataAttributeSelector(html.DATA_IMG_URL, url)], this.domNode).forEach((element) => {
					const refNode = this.getRefNode(element);
					if (refNode) {
						refNodes.add(refNode);
					}
				});
			});

			if (refNodes.size) {
				return this.redrawNodes([...refNodes]);
			}

			return this.redrawData();
		},

		redrawNewData: function() {
			// summary: redraw view with new data stores which is useful when stores can be created from the existing cache after an action
			return this._load(this._redrawNewData);
		},

		/**
		 * Redraw view with latest specs (attributes); for most views, this is same as redraw
		 * @return {Deferred}
		 */
		redrawSpecs: function() {
			return this.redraw();
		},

		refreshData: function() {
			// summary: react to node change
			return this._load(this._refreshData);
		},

		/**
		 * Reinitialize view's headings and attributes, which may be necessary if target class of view changes
		 */
		refreshAttributes() {
			const {defView, pathHintsContext} = this;
			const rcRoot = defView.m_rcRoot;

			this.attributeKeys = null;
			this.onViewInitializeAttributes();
			defView.populateHeadingsFromCards(rcRoot, pathHintsContext);
			this._resolveAttributeKeys();
		},

		redrawNode: function(/*DOMNode*/element) {
			// summary: refresh element based on change to node - by default defer
			// to refreshing entire widget
			return this._load(this._redrawNode, element);
		},

		/**
		 * Redraw nodes in view (same as redrawData unless this is table or gallery view)
		 * @param {HTMLElement[]} refNodes 
		 * @return {Deferred}
		 */
		redrawNodes(refNodes) {
			return this._load(this._redrawNodes, refNodes);
		},

		refreshWidget: function() {
			// summary: refresh widget according to current custom view
			return this._load(this._refreshWidget);
		},

		/**
		 * Called to indicate view is no longer the root view of the selected tab
		 */
		setTabNotSelected() {
			if (this.isValidView() && this.bViewed) {
				this.onTabSwitching();
				this.traverseViews((view) => {
					view.onTabSwitching();
					return true;
				});
			}
		},

		/**
		 * Called to indicate view is now the root view of the selected tab
		 */
		setTabSelected() {
			if (this.isValidView() && this.bViewed) {
				this.onTabSwitch();
				this.traverseViews((view) => {
					view.onTabSwitch();
					return true;
				});
			}
		},

		/**
		 * Suspend the view - only some views support such a state, so by default do nothing
		 * @param {boolean} suspended 
		 * @return {Deferred | void}
		 */
		suspendView(suspended) { },

		// widget lifecycle

		postMixInProperties: function() {
			// summary: initialize all class members
			this.inherited(arguments);

			const {defView} = this;
			var defTb = defView.defToolbar;
			var classes = [html.CSS_VIEW, html.CSS_VIEW + "-" + defView.getHtmlKey()];

			if (this["class"]) {
				classes.push(this["class"]);
			}

			if (defView.m_class) {
				classes.push(defView.m_class);
			}

			this["class"] = classes.join(" ");

			// determine source of toolbar plugins - either from toolbar as JSON string or from default plugins for view;
			// make sure to always copy defaults in case they are later changed (added to or deleted from)
			let {plugins} = this;

			if (defTb?.plugins != null) {
				plugins = csi.fromJson(defTb.plugins) || [];
			} else if (plugins) {
				plugins = csi.deepCopy(plugins);
			}

			// remove omitted plugins
			if (plugins && defTb?.pluginsOmit) {
				const omitSet = new Set(defTb.pluginsOmit.split(","));
				plugins = plugins.filter((p) => !omitSet.has(p.name || p));
			}

			this.plugins = plugins;
			this.imageSize = this.imageSize || this.getDefaultImageSize();
			this.relRoot = defView.getPageRelationship();

			this._deferreds = [];
			this._headingStores = {};
			this._matrixStores = {};

			// initialize view with root node
			// FIX: could possibly be called automatically if use _setUrlRootAttr?
			this.setViewNode(this.getViewNode(), true);

			// resolve classes, attributes and localization used in view
			this._resolveView();
			this._resolveAttributeKeys();
		},

		startup() {
			// summary: create and display widget

			// if already started, such as through call to placeAt, then just return _deferreds in progress
			if (this._started) {
				return this.getDeferred();
			}

			// console.debug("Startup view: " + this.toString());

			this.inherited(arguments);

			// create deferred to last for entire view load
			var dfd = new csiDeferred();
			this._deferreds.push(dfd);

			// enable view when done loading
			Tracking.addJob(lang.hitch(this, this.postAction, null), dfd);

			// create OpenTelemetry span
			const span = openTelemetryService.startSpan("c8.web.widget");
			//TODO: move assignment of user-related attributes into some kind of utility function
			span.setAttribute("c8.user.cnl", sessionStore.userUrl);
			span.setAttribute("c8.user.name", sessionStore.userName);
			// Track View Id and custom view as attributes
			span.setAttribute("c8.web.widget.view", this.defView.getKey());
			this.own(aspect.after(this, "onViewPreferences", () => this._onViewPreferences(), true));
			this._otelSpan = span;

			openTelemetryService.executeInContext(span, () => this._startup());

			return dfd;
		},

		_onViewPreferences() {
			this._otelSpan?.setAttribute("c8.web.widget.custom_view", this.customViewId);
		},

		_startup() {
			// summary: create and display widget
			const {domNode, isPreview, toolbarNode} = this;
			let dfdToolbar;

			// add support for actions
			html.setElementContext(domNode, this.getViewURL());

			this.connect(domNode, "onclick", this.onClick);
			this.connect(domNode, "ondblclick", this.onDoubleClick);

			// handle startup logic
			this.onViewCreate();

			// initialize toolbar (if any) for view and then start loading
			if (toolbarNode && !isPreview) {
				dfdToolbar = this.loadPlugins();
			} else {
				this._destroyToolbar();
			}

			// highlighted blocks no longer apply if reordering changes are ready to publish
			this.own(registerHoldPreSaveCallback(() => this.highlight()));

			// root views should listen for resize events
			if (!this.viewParent) {
				this.own(topic.subscribe("/csi/window/resize", () => this.resize()));
			}

			// resize view when there are DOM changes
			if (this.bResizeOnChange) {
				this.own(topic.subscribe("/csi/view/change", lang.hitch(this, this._onChangeDOM)));
			}

			// watch for changes to modal view state and react accordingly; popup and focused views are immune to this setting
			if (!this.isIsolatedView()) {
				this.own(state.watch("modalView", () => {
					this._disableView(this.disabled);
				}));
			}

			// listen for deletions from view cache
			this.own(aspect.after(this.cacheView, "onDelete", url => this._onDeleteCachedUrl(url), true));

			// add view tooltip for handling comment and heading tooltips - prevent tooltips when printing or previewing
			var tooltipRootNode = this.getTooltipRootNode();

			if (this.tooltipClass && tooltipRootNode && !isPreview && !state.get("print")) {
				this.tooltipView = new this.tooltipClass({
					connectId: [tooltipRootNode],
					view: this
				}, domConstruct.create("div", null, this.getTooltipNode()));
				this.tooltipView.startup();
			}

			// start view load process once toolbar is ready to go
			this.when(dfdToolbar, lang.hitch(this, this._onStartup));
		},

		getTooltipRootNode: function() {
			return this.headingsNode;
		},

		uninitialize: function() {
			// summary: custom widget tear-down behavior called from destroy()
			var a = this._deferreds;
			if (a) {
				// console.debug("uninitialize view: " + this.toString());
				this._bLoading = true;
				this._deferreds = null;
				_.each(a, function(dfd) {dfd.resolve();});
			}

			// ensure that any outstanding changes are published before changing views
			csiAction.stopNonModalAction(this.domNode);
			submitHoldWriter();

			// release location key if any
			if (this._keyLocation) {
				session.releaseLocationKey(this._keyLocation);
			}

			// tell view manager there is one less view
			csiView.deleteView(this);

			// clear circular references - being overly careful here
			if (this.tooltipView) {
				delete this.tooltipView.params;
				delete this.tooltipView.view;
			}

			delete this.cacheView;
			delete this._headingStores;
			delete this.viewParent;
			delete this.viewLayout;

			this.inherited(arguments);
		},

		when: function(/*Object?*/valueOrPromise, /*Function?*/callback, /*Function?*/errback) {
			// summary: when loading view, callback should check if view is still being displayed before continuing, as well
			// as catch any syntax errors encountered while rendering view
			var origCallback = callback;
			var origErrback = errback;

			if (!valueOrPromise || !valueOrPromise.then) {
				var valueOrig = valueOrPromise;
				valueOrPromise = new csiDeferred();
				valueOrPromise.resolve(valueOrig);
			}

			if (callback) {
				callback = lang.hitch(this, function() {
					if (this.domNode && this.isValidView(true)) {
						return origCallback.apply(null, arguments);
					}
				});
			}

			if (errback) {
				errback = lang.hitch(this, function() {
					if (this.domNode && this.isValidView(true)) {
						return origErrback.apply(null, arguments);
					}
				});
			}

			// add additional "then" to catch a javascript syntax error in the callback
			return valueOrPromise.then(callback, errback).then(null, lang.hitch(this, this.onViewError));
		},

		/**
		 * Request writer to submit operations to server; response will be cached with view
		 * @param {WriterCallbackFunction} callback  - function to write operations
		 * @param {boolean} [hold] - wait for 3 seconds before publishing operations
		 */
		writeOperations(callback, hold) {
			requestWriter(this.cacheView, callback, true, hold);
		},

		onViewCreate: function() {
			// summary: hook for logic to be executed when the widget is started
		},

		onViewDisable: function(/*Boolean*/disabled) {
			// summary: extension point for when view is enabled/disabled
		},

		/**
		 * Extension point called when view finishes loading
		 */
		onViewEnd() { },

		onViewError: function(/*Error?*/err) {
			// summary: view encountered an error, so reject and display error
			if (state.get("print")) {
				var dfd = this._deferreds && this._deferreds.shift();

				if (dfd) {
					// notify others that view has finished loading
					topic.publish("/csi/view/end", this);
					dfd.reject(err);
					return;
				}
			}

			if (this.domNode && this.isValidView()) {
				this.errorOnLoad = err;
				this._buildContentMessage(err);
			}

			this._endView();
		},

		/**
		 * Extension point to initialize the view's headings or attribute keys using custom logic
		 */
		onViewInitializeAttributes() { },

		/**
		 * Extension point called before view initializes toolbar with plugins
		 */
		onViewPlugins() { },

		/**
		* Extension point called after view is rendered, but just before it is formatted for printing
		* @return {Deferred|null}
		*/
		onViewPrint() { },

		/**
		 * Extension point to determine keys to skip when rendering view; typed or untyped keys are allowed
		 * @param {Set<C8AttributeKey>} skipSet 
		 */
		onViewResolveKeysToSkip(skipSet) { },

		/**
		 * Transform keys used to generate attribute specs from visible keys
		 * @param {ICustomViewAttribute[]} visibleKeys
		 * @return {ICustomViewAttribute[]}
		 */
		onViewTransformVisibleKeys(visibleKeys) {
			return visibleKeys;
		},

		/**
		 * Extension point called when response to root request is received
		 * @param {Response} res
		 */
		onViewRootResponse(res) { },

		/**
		* Extension point called before view rendering starts
		* @param {Deferred[]} dfds
		*/
		onViewStart(dfds) { },

		/**
		 * Extension point called when root data is cached and ready to build view
		 */
		onViewStartBuild() { },

		/**
		 * Extension point for acting on view toolbar
		 * @param {Toolbar} tb
		 */
		onViewToolbar(tb) { },

		/**
		 * Prepend toolbar with switchboard plugin
		 * @param {Action} switchboard
		 * @param {string} [contextType]
		 */
		addSwitchboard: function(switchboard, contextType) {
			if (this.plugins.length) {
				this.plugins.unshift("|");
			}

			this.plugins.unshift(switchboard);
			this._switchboard = switchboard.m_id;

			// separate custom view context from normal context
			if (this.prefViewContext == null) {
				this.prefViewContext = this.viewParent ? this.viewParent.getViewNode().$Type : contextType;
			}
		},

		getDeferred: function() {
			// summary: return last deferred in array
			return this._deferreds && this._deferreds.last();
		},

		/**
		 * return deferred which will resolve when all deferreds gets resolved
		 * if no deferreds on view - create new dummy deferred, resolve and return it
		 * @returns
		 */
		getAllDeferreds: function() {
			return all(this._deferreds);
		},

		/**
		 * Extract heading information from event
		 * @param {Event} e
		 * @return {EventHeadingContext}
		 */
		getEventHeadingContext(e) {
			const {headingsNode} = this;

			if (!headingsNode) {
				return null;
			}

			const refNode = csiHeading.findParentHeadingNode(e.target, headingsNode);
			if (!refNode) {
				return null;
			}

			const ctx = this.getContext();
			const h = this.getHeading(csiHeading.getHeadingNodeMarker(refNode, ctx), refNode);
			if (!h) {
				return null;
			}

			const keyContainer = ctx.keyValue = csiHeading.getHeadingValueKey(refNode);

			const ren = csiHeading.getHeadingRendererInContext(ctx, h);
			if (!ren) {
				return null;
			}

			return {
				ctx,
				h,
				keyContainer,
				refNode,
				ren,
			};
		},

		getSwitchboard: function() {
			// summary: return switchboard plugin if one
			return this._switchboard ? this.getPlugin(this._switchboard) : null;
		},

		loadPlugins: function() {
			// summary: initialize toolbar with all plugins, returning Deferred necessary to run before view can continue to load
			var tb = this.toolbarNode;
			var defView = this.defView;
			var defTb = defView.defToolbar;
			var ctx = this.getContext();
			var plugins = this.plugins;
			var rel = this.relRoot;

			// add actions to plugins array
			if (defTb?.m_actions) {
				if (plugins.length) {
					plugins = plugins.concat("|");
				}

				plugins = plugins.concat(defTb.m_actions);

				// if view is read-only, then toolbar actions should not be shown if they cannot be accessed
				if (ctx.isReadOnly()) {
					_.each(defTb.m_actions, function(a) {
						a.bValidateWithAccess = true;
					});
				}

				defTb.m_actions = null;
			}

			// re-order plugins to make sure the right aligned plugins are first in list so styling is correct
			if (this.pluginsRight) {
				plugins = csiViewUtil.reorderRightPlugins(plugins, this.pluginsRight);
			}

			// add NewActions plugin to beginning of toolbar
			if (rel && rel.hasNewAction() && !ctx.isReadOnly()) {
				plugins.unshift("|");
				plugins.unshift("ToolbarNewActions");
			}

			this.plugins = plugins;
			this.onViewPlugins();

			// populate toolbar widgets
			var dfd = this._hasToolbar() ? tb.setView(this) : null;
			if (dfd) {
				// if view has been inserted into slim view, then move toolbar up to view defined as slim
				if (defView.isSlim()) {
					var viewParent = this.getToolbarContainerView();
					if (viewParent && !viewParent.toolbarNode) {
						// display toolbar in what would be parent view's toolbar location
						domConstruct.place(tb.domNode, viewParent.domNode, "first");

						// DOJO INTERNAL: add to list of supporting widgets so it will be destroyed when view is destroyed
						this._supportingWidgets.push(tb);

						// notify parent view of this toolbar, even though still owned by child view
						viewParent.onViewToolbar(tb);
					}
				}

				this.onViewToolbar(tb);
				tb.disablePlugins();
				return dfd;
			}

			// if toolbar is empty, then delete
			if (defView.isSlim() || !defTb) {
				this._destroyToolbar();
			}

			return null;
		},

		/**
		 * Return true if view actions should be rendered; by default, return false when locationIsDormant is true in non-popup views
		 * @return {boolean}
		 */
		shouldRenderViewActions: function() {
			return this.bPopup || !routerStore.locationIsDormant;
		},

		_addToolbarActions: function(/*Toolbar*/tb) {
			// summary: add view-specific toolbar actions menu if present
			var ctx = this.getContext();
			// Do not add "Actions" toolbar if popup
			if (ctx.bPopup) {
				return;
			}
			var defToolbar = this.defView.defToolbar;
			var actions = defToolbar && defToolbar.getMenuActions();

			if (actions) {
				// if view is read-only, then toolbar actions should not be shown if they cannot be accessed
				if (ctx.isReadOnly()) {
					array.forEach(actions, function(a) {
						a.bValidateWithAccess = true;
					});
				}

				// convert array of actions into menu object structure
				var children = actions;
				var submenus = csi.hashArray(actions, "parentMenu", null, null, true);

				if (_.size(submenus) > 1) {
					children = [];

					_.each(actions, function(a) {
						var id = a.parentMenu;
						if (id) {
							var submenu = submenus[id];
							if (submenu) {
								children.push({name: locale.resource(id), children: submenu});
								submenus[id] = null;
							}
						} else {
							children.push(a);
						}
					});
				}

				tb.addPlugin(ctx, {name: "Actions", children: children});
			}
		},

		_hasToolbar: function() {
			// summary: disable toolbar if view is defined as slim
			return !this.defView.isSlim(true);
		},

		/**
		 * Invoke action
		 * @param {DOMNode} node
		 * @param {Action} a
		 */
		_onElementAction: function(node, a) {
			if (a) {
				csiAction.invoke(this, node, a);
			}
		},

		/**
		 * Action button click
		 * @param {DOMNode} node
		 * @param {Action} a
		 * @param {Event} e
		 */
		_onElementActionClick: function(node, a, e) {
			html.removeFocus();
			this._onElementAction(node, a);
			event.stop(e);
		},

		/**
		 * Resize view when generic change to DOM occurs, except when printing. Any DOM changes while printing
		 * will not affect non-print views, and print views should not rely on such events to call resize.
		 */
		_onChangeDOM: function() {
			if (!state.get("print")) {
				this.resize();
			}
		},

		/**
		 * Set view to refresh on tab switch if not currently shown and url being deleted is a result in the view
		 * @param {string} url
		 */
		_onDeleteCachedUrl: function(url) {
			const {domNode, refreshOnTabSwitch} = this;

			if (!refreshOnTabSwitch && !domNode.offsetHeight) {
				this.refreshOnTabSwitch = !!this.getRefNodeByUrl(url);
			}
		},

		/**
		 * Root request is now processed and ready to start rendering view
		 */
		_onPreViewSearch: function() {
			this.onViewStartBuild();
			this._buildView();
		},

		_onRefreshAttribute: function(/*Context*/ctx, /*Heading*/h, /*Renderer*/ren, /*DOMNode*/refNode, /*Boolean*/bPendingChange) {
			// summary: notify view of change to attribute
			if (!bPendingChange) {
				this.publishDomChange(refNode);
			}
		},

		_onResize: function() {
			// summary: resize view to fit available space
		},

		/**
		 * Start rendering view on startup
		 */
		_onStartup: function() {
			this._renderView();
		},

		_redraw: function() {
			// summary: re-render view
			this.highlight();
			this._preView();
			this.onRedraw();
		},

		/**
		 * Extension point called when redraw view
		 */
		onRedraw: function() { },

		_redrawData: function() {
			// summary: re-render data in view (not necessarily headers)
			this._redraw();
		},

		_redrawNewData: function() {
			// summary: re-render data in view, but with new stores
			this.onChangeData();
			this._resetStores();
			this._redraw();
		},

		/**
		 * Redraw data in view
		 * @param {HTMLElement[]} refNodes 
		 */
		_redrawNodes(refNodes) {
			this._redrawData();
		},

		/**
		 * Invoke function to load view in some way, managing a deferred
		 * that will exist for the duration of the load
		 * @param {VoidFunction} func
		 * @return {Deferred}
		 */
		_load(func, ...args) {
			this.freezePopupScrollNode(true);

			// check to make sure view is still valid before starting new deferred action
			if (!this.isValidView()) {
				return null;
			}

			// console.debug("(re)loading view: " + this.toString());

			// create deferred to last for entire view load - in some cases
			// _postView could be called synchronously as part of function call
			// below, so make sure to store and return local variable to deferred
			// in case this._deferred is reset
			var dfdOld = this.getDeferred();
			var dfd = new csiDeferred();

			func = func || this._refreshWidget;

			this._deferreds.push(dfd);
			this._bLoading = true;

			when(dfdOld, () => {
				this._bLoading = false;
				func.apply(this, args);
			});

			return dfd;
		},

		_refreshData: function() {
			// summary: reset data stores and re-render view - do not default to _redrawData as that can be optimized to draw with existing data,
			// whereas here we are clearing all data
			this.set("refreshTimestamp", new Date().getTime());
			this.onChangeData();
			this._resetStores();
			this._redrawData();
		},

		_refreshWidget: function() {
			// summary: reset view configuration and data
			this._refreshData();
		},

		/**
		 * Start rendering view
		 */
		_renderView: function() {
			if (this.isStarted() && this.isValidView(true)) {
				if (this.bViewed) {
					// don't call refreshWidget so that we don't get view-specific behavior that may try to keep state consistent across refreshes;
					// in other words, switching custom views is a true refresh (for some tables, refreshWidget will capture currently expanded nodes
					// and try to restore them as it refreshes)
					this._refreshWidget();
				} else {
					this._preView();
				}
			}

			return this.getDeferred();
		},

		/**
		 * Render view actions to "actionsNode"
		 * @param {boolean} [bRenderDisabled] - set to true if disabled actions should be rendered
		 */
		_renderViewActions: function(bRenderDisabled) {
			// FIX: change this to be true context of view? Need correct context for aggregate action and others...
			var ctx = this.getContext(this.getViewURL());

			var actions = this.defView.m_defActions;
			if (actions && !ctx.bPrint) {
				// if action node not set by template or while building view,
				// create node at bottom of view
				if (this.actionsNode) {
					html.destroyChildren(this.actionsNode);
				} else {
					this.actionsNode = domConstruct.create("div", {"class": "csiActionsNode"}, this.getActionContainer());
				}

				// if no form, then do not allow submit actions
				if (!this.getForm()) {
					array.forEach(actions.getActions(), function(a) {
						a.m_bSubmit = false;
					});
				}

				html.setVisible(this.actionsNode, actions.toHtml(ctx, this.actionsNode, bRenderDisabled));
				this._dialogCheckboxes = _.reduce(this.dialogActionCheckboxes, function(obj, params) {
					var id = params.id;
					var widgetParams = {};
					widgetParams.label = locale.resource(id);
					widgetParams.tooltip = locale.tooltip(id);
					obj[id] = new CheckboxDialogAction(widgetParams).placeAt(this.actionsNode.lastChild, "first");
					return obj;
				}.bind(this), {});
			} else if (this.actionsNode) {
				html.setVisible(this.actionsNode, false);
			}
		},

		_preView: function() {
			// summary: start rendering of view
			var dfds = [];

			//console.debug("Pre view: " + this.toString());
			topic.publish("/csi/view/preview", this);
			this._resolveAttributeSpecs();
			this.onViewStart(dfds);
			topic.publish("/csi/view/start", this);
			this.bViewed = true;
			delete this.errorOnLoad;

			// add CSS for paper size and orientation if view is specifically designed for printing, or in process of printing
			if (this.bPrintView || (state.get("print") && !this.getToolbarContainerView())) {
				this.applyPrintCSS();
			}

			// lookup heading renderers to make sure they are loaded if the list has not been processed
			dfds.push(this.loadViewClasses());

			this.when(all(dfds), () => {
				// continue building view once all heading renderers are loaded
				var urlView = this.getViewURL();
				var switchboard = this.getPlugin(this._switchboard);
				if (switchboard && this.cacheView.isDeleted(urlView) && (urlView == switchboard.get("value"))) {
					// if switchboard value has been deleted, then refresh switchboard; need to first end current
					// load before starting another one
					when(this.getDeferred(), lang.hitch(switchboard, switchboard.refreshStore, null, true));
					this._endView();
				} else {
					// otherwise, perform root search request unless omitted and continue with building this
					var request = this._getRootRequest(this.getViewNode());
					if (request) {
						request = this.cacheView.reduceNodesRequest(request, this.get("refreshTimestamp"));
					}

					if (request) {
						this.annotateViewRequest(request);

						this.when(request.go(), (res) => {
							this.cacheView.cacheResponse(res);
							this.onViewRootResponse(res);
							this._onPreViewSearch();
						});
					} else {
						this._onPreViewSearch();
					}
				}
			});
		},

		_buildContentMessage: function(/*Error?*/err) {
			// summary: render error message
			var errorMessage = err ? err.message : (this.contentMessage || locale.resource("ErrorLoadingView"));
			var contentNode = this.contentNode;

			if (contentNode && contentNode.domNode) {
				contentNode = contentNode.domNode;
			}

			if (contentNode) {
				// replace content with message
				html.destroyChildren(contentNode);
			} else {
				// hide view and insert message before view
				html.setVisible(this, false);

				contentNode = html.create("div", {
					"class": "csiViewErrorMessage"
				}, this.domNode, "before");
			}

			html.create("div", {
				className: "csiViewContentMessage",
				innerText: errorMessage
			}, contentNode);

			// if exception, then provide link to show exception dialog if user is an admin (and not printing)
			var exception = err && err.csiErrorException;
			if (exception && state.get("admin") && !state.get("print")) {
				new Button({
					"class": html.CSS_BUTTON_LINK,
					label: _.escape(locale.resource("ShowExceptionDialog")),
					onClick: function() {
						message.errorDialog({
							bShowException: true,
							exception: exception,
							message: errorMessage
						});
					}
				}).placeAt(contentNode);
			}
		},

		_buildView: function() {
			// summary: render view contents
			if (this.get("contentMessage")) {
				this._buildContentMessage();
				this.set("contentMessage", "");
				this._endView();
			} else {
				this._postView();
			}
		},

		/**
		 * Create SpecBuilder for view if necessary
		 * @param {Relationship} [rel]
		 * @return {SpecBuilder}
		 */
		createSpecBuilder(rel) {
			const {configuredHeadings, defView, relRoot} = this;
			const {pathHintsContext, prefPaths} = defView;
			const ctx = this.getContext();

			rel ||= relRoot;

			return new SpecBuilder(
				rel ? rel.getTargetClass() : defView.getPageClass(),
				ctx,
				rel ? rel.pathHintsContext : pathHintsContext,
				rel?.targetPath,
				rel ? rel.m_prefPaths : prefPaths,
				configuredHeadings
			);
		},

		/**
		 * Return list of spec builders for view
		 * @return {SpecBuilder[]}
		 */
		getSpecBuilders() {
			return [this.specBuilder];
		},

		_deleteViewLayout: function() {
			// summary: delete layout view
			if (this.viewLayout) {
				this.viewLayout.destroyView();
				this.viewLayout = null;
			}
		},

		/**
		 * Destroy toolbar if it exists
		 */
		_destroyToolbar() {
			this.toolbarNode?.destroyRecursive();
			this.toolbarNode = null;
		},

		_postView: function() {
			// summary: complete building of view
			if (!this.isValidView(true)) {
				return null;
			}

			// console.debug("Post view: " + this.toString());

			var tb = this.toolbarNode;

			// restrict actions when location node is dormant, except when in popup view
			if (this.shouldRenderViewActions()) {
				// process view actions
				this._renderViewActions();

				// process view toolbar
				if (tb && !this._bToolbarActions) {
					this._addToolbarActions(tb);
					this._bToolbarActions = true;
				}
			}

			// validate toolbar actions and remove invalid ones
			if (tb && !tb.validateAndRemovePlugins(this.getContext())) {
				this._destroyToolbar();
			}

			// enable toolbar plugins once view is redrawn
			this.reenableToolbar();

			// do any deferred post-processing
			var ctx = this.getContext(this.getViewURL());
			var dfds = [];

			this._postViewDeferred(ctx, dfds);

			return this.when(all(dfds), () => this._endView());
		},

		_postViewDeferred: function(/*Context*/ctx, /*Deferred[]*/dfds) {
			// summary: view-specific post-processing
		},

		_endView: function() {
			// summary: view is fully loaded so notify others

			// if not printing, then broadcast DOM change event to all
			if (!state.get("print")) {
				this.publishDomChange();
			}

			// console.debug("End view: " + this.toString());

			var dfd = this._deferreds && this._deferreds.shift();
			if (dfd) {
				dfd.resolve();
			}

			if (this.domNode) {
				this.onViewEnd();
			}

			if (this._otelSpan) {
				this._otelSpan.end();
				this._otelSpan = null;
			}

			// notify others that view has finished loading
			topic.publish("/csi/view/end", this);
		},

		_resetStores: function() {
			// summary: reset data stores
			this._headingStores = {};
			this._matrixStores = {};
		},

		/**
		 * Resize toolbar if it exists
		 */
		_resizeView() {
			if (this.isLoadingView()) {
				return;
			}
			this.freezePopupScrollNode(false);
			this.toolbarNode?.resizeToolbar();

			// view-specific resizing
			this._onResize();
		},

		/**
		 * Determine default set of attribute keys in the view if not already specified
		 */
		_resolveAttributeKeys() {
			const {attributeKeys, defView} = this;
			const {m_headings, m_images} = defView;

			// mark headings in view so that they are made visible even if hidden in model
			def.traverseHeadings(m_headings, (h) => {
				h.bForceVisible = true;
			});

			def.traverseHeadings(m_images, (h) => {
				h.bForceVisible = true;
			});

			this.configuredHeadings = m_headings;
			this.specBuilder = this.createSpecBuilder();

			if (m_headings && !attributeKeys) {
				const specBuilders = this.getSpecBuilders();
				const defaultKeys = generateAttributeKeysFromHeadings(m_headings, specBuilders);
				this.set("attributeKeys", defaultKeys);
			}
		},

		/**
		 * Create specs from visible and hidden attribute keys
		*/
		_resolveAttributeSpecs() {
			const {attributeKeys, defView, hiddenKeys, specBuilder} = this;
			if (attributeKeys) {
				const specs = this.attributeSpecs = new Specs();
				const headings = defView.m_headings = [];

				const skipSet = new Set();
				this.onViewResolveKeysToSkip(skipSet);

				const visibleKeys = getVisibleAttributeKeys(attributeKeys, hiddenKeys, skipSet);
				const finalKeys = this.onViewTransformVisibleKeys(visibleKeys);
				generateSpecsFromAttributeKeys(specs, specBuilder, finalKeys);

				// populate headings from specs - attribute group headings are not important; these are now just needed to support functions like findHeading, etc.
				// and so may not be needed in future
				specs?.traverse(false, (spec) => {
					if (!spec.isGroup) {
						headings.push(spec.heading);
					} else if (!spec.isMatrix) {
						spec.subs.forEach((subSpec) => {
							headings.push(subSpec.heading);
						});
					} else {
						headings.push(spec.heading);
					}
				});
			}
		},

		_resolveView: function() {
			// summary: resolve resources for view
			const {bSkipRender, cacheView, defView, prefKey, relRoot, separateSupplierViews} = this;
			const node = this.getViewNode();
			const rcRoot = defView.m_rcRoot || reflection.getNodeClass(node) || reflection.getClass(defView.getTypeNode());

			defView.bPreferences = !!this.supportsCustomViews();
			defView.m_rcRoot = rcRoot;
			this.onViewInitializeAttributes();

			if (!bSkipRender) {
				defView.resolve(rcRoot, node);
			}

			// ensure that if root Relationship has URL then that node is in view's cache;
			// assumption is that URL will never appear on anything other than root Relationship
			// FIX: we could refactor code so that cache is passed into resolve function, but I'll leave that for another day
			if (relRoot?.m_url) {
				resolver.resolveNode(relRoot.m_url, false, cacheView);
			}

			if (separateSupplierViews && !prefKey && (sessionConstants.userType == "Supplier")) {
				this.prefKey = defView.getKey("SupplierUser");
			}

			this._bResolved = true;
		},

		_insertView: function(/*String|View*/idView, /*Node*/node, /*Object?*/params, /*Boolean*/bSync) {
			// summary: insert view into this one - view can either be id or view definition
			var defView = (typeof idView === "string") ? def.getNodeViewDef(node, idView, true) : idView;
			if (!defView) {
				console.assert(!node, "View could not be found: " + idView);
				return null;
			}

			const {
				bFocused,
				bInSwitchboard,
				bPopup,
				flexHeight,
				flexHeightPropagetesToChildren,
				isPreview,
			} = this;

			params = {
				bFocused,
				bInSwitchboard,
				bPopup,
				isPreview,
				viewParent: this,
				...params,
			};

			if (flexHeightPropagetesToChildren) {
				params.flexHeight = flexHeight;
			}

			// give parent view opportunity to set parameters on inserted view
			this.onInsertViewParams(defView, node, params);

			// share cache unless either view requires its own
			if (!params.cacheView && !this.defView.bOwnCache && !defView.bOwnCache) {
				params.cacheView = this.cacheView;
				params.refreshTimestamp = this.get("refreshTimestamp");
			}

			// apply form data if any
			// FIX: eventually store formData on csi/view/Widget rather than csi/def/View in which case it works better as a param
			if (params.formData) {
				defView.setFormData(params.formData);
				params.formData = null;
			}

			// if inserting into slim view, then make inserted view slim as well
			if (this.defView.isSlim()) {
				if (!defView.isSlim()) {
					defView.m_bSlimOverride = true;
				}

				// if printing, then page size of slim views should be 0
				if (state.get("print")) {
					defView.m_nPageSize = 0;
				}
			}

			// if inserting into read-only view, then make inserted view read-only as well
			if (this.defView.isReadOnly()) {
				defView.setReadOnly(true);
			}

			// finally create new view
			if (bSync) {
				return csiView.newView(defView, node, params);
			}

			return csiView.newViewDeferred(defView, node, params).then(lang.hitch(this, function(view) {
				if (view) {
					if (this.isValidView(true)) {
						return view;
					}

					view.destroyView();
				}

				return null;
			}));
		},

		_insertAndCreateView: function(/*String|View*/idView, /*Node*/node, /*DOMNode*/domNode, /*Object*/params) {
			// summary: insert view as above and then create, returning deferred to be resolve after loading entire view
			var dfd = new csiDeferred();
			var dfdView = this._insertView(idView, node, params);
			if (dfdView) {
				dfdView.then(lang.hitch(this, function(view) {
					if (view) {
						when(view.placeAt(domNode).startup(), function() {
							dfd.resolve(view);
						});
					} else {
						dfd.resolve(null);
					}
				}));
			} else {
				dfd.resolve(null);
			}

			return dfd;
		},

		_onRootDependencyPaths: function(/*String[]*/paths) {
			// summary: extension point for derived views to add own paths
		},

		_onRootRequest: function(/*Request*/request) {
			// summary: extension point to modify request
		},

		_getRootRequest: function(/*Node*/node) {
			// summary: return request used to query for root object of view with dependencies;
			// bRefresh indicates the view is being refreshed, so force query if root request is necessary for view
			var request = node ? comm.getNodeRequest(node.$URL, null, true) : null;
			if (!request) return null;

			var defView = this.defView;
			var paths = [].concat(defView.m_depPaths || []);

			// find all paths used in attibute headings - ignore headings of dynamic columns
			// since are not relative to the root node
			if (this.isShowingRootNode()) {
				def.getHeadingsDependencyPaths(defView.getHeadings(), paths);
				def.getHeadingsDependencyPaths(defView.m_images, paths);
			}

			// get action paths
			if (defView.defToolbar) {
				_.each(defView.defToolbar.getAllActions(), function(a) {
					def.getActionDependencyPaths(a, paths);
				});
			}

			if (defView.m_defActions) {
				_.each(defView.m_defActions.m_actions, function(a) {
					def.getActionDependencyPaths(a, paths);
				});
			}

			// add view-specific paths
			this._onRootDependencyPaths(paths);
			request.addDependencyPaths(paths);

			// add parameter to get extra file information if so configured
			if (this.showFileSizes) {
				request.addParameter("Ext.File.Info", true);
			}

			// call extension point to add further parameters to request
			this._onRootRequest(request);

			return request;
		},

		createHeadingNamesStore: function(/*Context*/ctx, /*Heading|Field*/h, /*Any*/value, /*Relationship?*/r, /*String?*/keyStore, /*Object?*/storeOptions) {
			// summary: create store for use in view
			storeOptions = storeOptions ? _.clone(storeOptions) : {};

			// resolve relationship relative to current node
			if (r) {
				csiStore.namesOptionsByRelationship(ctx, r, storeOptions);
			}

			// add store options based on given heading and value
			if (h) {
				csiStore.nameOptionsByHeading(ctx, h, value, storeOptions);
			}

			// add locale to store
			if (ctx.locale) {
				storeOptions.loc = ctx.locale;
			}

			// create store
			var store = new Names(storeOptions);

			// cache store for later reuse
			if (!keyStore) {
				// determine url to use as key to store - if relationship has filter with expression, then use current context
				// since that will be used to evaluate the expression(s)
				if (r && r.m_url && !r.hasFilterExpression()) {
					keyStore = r.m_url;
				} else {
					keyStore = ctx.getNodeURL();
				}
			}

			this.annotateViewStore(store);
			this.cacheHeadingStore(h, keyStore, store);

			return store;
		},

		cacheHeadingStore: function(/*Heading*/h, /*String*/keyStore, /*Store*/store) {
			// summary: cache store for later reuse
			if (h._rca?.isDelayedSearch) {
				return;
			}
			var keyHeading = this._getHeadingStoreKey(h);
			if (keyHeading) {
				var obj = this._headingStores[keyHeading];
				if (!obj) {
					obj = this._headingStores[keyHeading] = {};
				}

				obj[keyStore] = store;
			}
		},

		_getHeadingStoreKey: function(/*Heading|Field*/obj) {
			// summary: return key to be used to cache heading store
			return obj.getKeyDP?.() ?? obj.getKey();
		},

		getHeadingStore: function(/*Heading*/h, /*String*/keyStore) {
			// summary: return cached store for heading editing
			var keyHeading = this._getHeadingStoreKey(h);
			var obj = this._headingStores[keyHeading];
			return obj ? obj[keyStore] : null;
		},

		cacheMatrixStore: function(/*String*/id, /*Store*/store) {
			// summary: cache store for matrix
			this._matrixStores[id] = store;
			return store;
		},

		getMatrixStore: function(/*String*/id) {
			// summary: return cached store for matrix
			return this._matrixStores[id];
		},

		refreshHeadingStore: function(/*Heading*/h, /*String?*/keyStore) {
			// summary: reset store holding query results for editing heading
			const keyHeading = h && this._getHeadingStoreKey(h);
			if (keyHeading) {
				const {_headingStores} = this;
				const obj = _headingStores[keyHeading];

				if (keyStore == null) {
					delete _headingStores[keyHeading];
				} else if (obj) {
					Object.keys(obj).forEach((key) => {
						if (key.indexOf(keyStore) == 0) {
							delete obj[key];
						}
					});
				}
			}
		},

		refreshHeading: function(/*Heading*/h, /*String?*/urlNode, /*DOMNode?*/omitNode, /*DOMNode?*/rootNode) {
			// summary: refresh rendering of heading with given key - this will NOT work with sub-headings;
			// if url passed, then only refresh heading in given node context;
			// if rootNode passed, then only refresh dom nodes within given dom context
			if (!h) return;

			var a = csiHeading.findHeadingNodes(rootNode || this.headingsNode || this.domNode, h);
			if (a) {
				a.forEach(function(refNode) {
					if (!omitNode || (refNode != omitNode)) {
						var url = html.getElementContext(refNode);

						if (!urlNode || (url == urlNode)) {
							const ctx = this.getContext();
							ctx.readNodeContext(refNode);
							this.refreshHeadingCell(ctx, h, refNode);
						}
					}
				}, this);
			}
		},

		refreshHeadingCell: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*Boolean*/bPendingChange) {
			// summary: redraw cell in view
			const qr = this.getQueryResultByDomNode(refNode);
			qr?.preProcessContext(ctx);

			const ren = csiHeading.refreshHtml(ctx, h, refNode);
			this._onRefreshAttribute(ctx, h, ren, refNode, bPendingChange);

			qr?.postProcessContext(ctx);
		},

		editHeading: function(/*DOMNode*/rootNode, /*Boolean*/bTimeout, /*Object?*/params) {
			// summary: edit first heading under given node - timeout is sometimes necessary
			// to give calling action time to finish
			return csiHeading.findHeadingNodes(rootNode).some(function(e) {
				var id = html.getElementAction(e);
				if (id) {
					// check if the element is displayed on view or not
					if (!html.isShown(e)) {
						return false;
					}

					if (bTimeout) {
						// delay invoking edit action for some time to allow for other post-processing of action to complete;
						// this could include processing a location change to take effect when working with a switchboard (DE25042)
						_.delay(function(view) {
							Tracking.addJob(function() {
								csiAction.invoke(view, e, id, params);
							});
						}, 200, this);
					} else {
						csiAction.invoke(this, e, id, params);
					}

					return true;
				}
			}, this);
		},

		findNextHeadingNode: function(/*DOMNode*/node, /*String*/key, /*Boolean*/bShift) {
			// summary: default keyboard navigation is straight DOM traversal
			return csiHeading.getNextTabStop(this.headingsNode, node, key, bShift);
		},

		_disableView: function(/*Boolean*/disabled) {
			// summary: disable elements of the view

			// Find all controls by class name and set disabled state
			const setDisabledBtn = (cssClass, domNode) => {
				html.queryDom([cssClass], this.headingsNode).forEach((dom) => {
					const btn = registry.byNode(dom);
					html.setDisabled(btn, disabled);
				});
			}

			// if there is a modal view, then disable all other views
			var modalView = state.get("modalView");

			//for canvasWidget modalView have to be there even while editing 3DViewer inorder to move 3d snapshots to canvas
			if (!disabled && !this.threeDitems && !this.bPopup && modalView && (modalView != this) && modalView.disableOtherViewsWhenModal) {
				disabled = true;
			}

			// FIX: eventually change toolbar to support set("disabled", disabled);
			var tb = this.toolbarNode;
			if (tb && disabled) {
				tb.disablePlugins();
			} else if (tb) {
				tb.enablePlugins();
			}

			// enable/disable other actions in view
			if (this.bDisableContentActions && this.headingsNode) {
				html.queryDom([".csiAction.csiActionIcon"], this.headingsNode).forEach(function(domNode) {
					domClass.toggle(domNode, "csi-action-disabled", disabled);
				});

				// enable/disable action rows buttons
				setDisabledBtn(".csi-actions-new");
				// Set disabled state for "..." menus
				setDisabledBtn(".csi-row-actions-menu");
			}

			// determine visibility and enabled status of action buttons
			this._setActionButtonsDisabled(disabled);

			// determine visibility and enabled status of action checkboxes
			this._setActionCheckboxesDisabled(disabled);

			// hide any view tooltip
			if (disabled) {
				Tooltip.hide();
			}

			// allow extensions to this method
			this.onViewDisable(disabled);
		},

		_setActionButtonsDisabled: function(/*Boolean*/disabled) {
			// summary: control enabled/disabled status of (dialog) action buttons
			_.each(this.getActionButtons(), function(button) {
				var idAction = html.getElementAction(button.domNode);
				var showAction = this.canShowButton(idAction);
				var disableAction = disabled || !showAction;

				if (!disableAction) {
					disableAction = !this.canEnableButton(button, idAction);
				}

				html.setDisabled(button, disableAction);
				html.setVisible(button, showAction);
			}, this);
		},

		/**
		 * Control enabled/disabled status of (dialog) action checkboxes
		 * @param {boolean} disabled
		 */
		_setActionCheckboxesDisabled: function(disabled) {
			_.each(this.dialogActionCheckboxes, function(config) {
				var checkbox = this._dialogCheckboxes ? this._dialogCheckboxes[config.id] : null;
				if (checkbox) {
					var showAction = this.canShowActionCheckbox(config);
					var disableAction = disabled || !showAction;

					if (!disableAction) {
						disableAction = !this.canEnableActionCheckbox(config);
					}

					html.setDisabled(checkbox, disableAction);
					html.setVisible(checkbox, showAction);
				}
			}, this);
		},

		/**
		 * Enable or disable view actions/toolbar
		 * @param {boolean} disabled
		 */
		_setDisabledAttr(disabled) {
			if (disabled && (disabled === this.disabled)) {
				// if already disabled, then no reason to disable again;
				// if enabled though, and setting to enabled again, it is possible some actions
				// are now enabled that maybe weren't enable before due to access checks. So in that
				// case, we do run the enable logic again
				return;
			}

			// view should still be disabled if it is suspended until scrolled into view;
			// NOTE: this is intentionally below the code above as we want the view to officially be enabled, but the action widgets disabled
			if (this.suspendedUntilScrolledIntoView && !disabled) {
				disabled = true;

				// we still want the new actions to show up, just in disabled state
				this.getPlugin("ToolbarNewActions")?.setActions();
			}

			this._disableView(disabled);
			this._set("disabled", disabled);
		},

		/**
		 * Add/remove CSS class when view changed to/from flex height
		 * @param {boolean} value
		 */
		_setFlexHeightAttr: function(value) {
			this._set("flexHeight", value);
			this.domNode.classList.toggle("csi-flex-view", !!value);
		},

		_setImageSizeAttr: function(/*String*/value) {
			// summary: image size should always have some value, so initialize to default if not specified;
			// if no value specified and value already exists, then keep existing value (i.e. favor imageSize over defaultImageSize)
			if (value || !this.imageSize) {
				this._set("imageSize", value || this.getDefaultImageSize());
			}
		},

		/**
		 * Force a refresh when the file sizes attribute is changed
		 * @param {boolean} value 
		 */
		_setShowFileSizesAttr(value) {
			this._set("showFileSizes", value);

			if (value) {
				this.set("refreshTimestamp", new Date().getTime());
			}
		},

		_setSplitterTypeAttr: function(value) {
			this._set("splitterType", value);

			// if printing, mark views that can be split with a special class
			if (state.get("print")) {
				html.setDataAttribute(this.domNode, html.PRINT_SPLITTER, value);
			}
		},

		// Controller functions

		onActionSetValue: function(/*Controller*/ac) {
			// summary: set properties in actionValue on view
			this.set(ac.actionValue);
		},

		/**
		 * Redraw entire view using data already in cache (without re-querying for data).
		 * (i.e. for table views this will redraw entire table, header and body)
		 * @param {Controller} ac - csi/action/Controller
		 * @return {Deferred}
		 */
		postActionRedraw: function(ac) {
			var id = ac.refNode ? this.getRefNodeId(ac.refNode) : null;
			var dfd = this.redraw();

			if (id) {
				dfd.then(lang.hitch(this, function() {
					var refNode = this.getRefNodeById(id);
					if (refNode) {
						this.scrollIntoView(refNode);
					}
				}));
			}

			return dfd;
		},

		/**
		 * Redraw all data in view using data already in cache.
		 * (i.e. for table views this will redraw all table body rows - no changes to table header or number of columns)
		 * @param {Controller} ac - csi/action/Controller
		 * @return {Deferred}
		 */
		postActionRedrawData: function(ac) {
			return this.redrawData();
		},

		postActionRedrawHeading: function(/*Controller*/ac) {
			// summary: redraw heading
			return this.refreshHeading(ac.heading, ac.m_urlNode, null, ac.refNode);
		},

		postActionRedrawNewData: function(/*Controller*/ac) {
			// summary: redraw view with cached data, but recreate stores (hopefully from cache)
			return this.redrawNewData();
		},

		postActionRefreshNode: function(/*Controller*/ac) {
			// summary: redraw content for entire node
			var cache = ac.getCache();
			var nodeURL = ac.getNodeURL();
			if (cache && nodeURL) {
				cache.removeNode(nodeURL);
				cache.addNode(nodeURL);
			}
			return this.redrawNode(ac.refNode || ac.m_element);
		},

		/**
		 * Redraw data for Node affected by Action using data already in cache.
		 * (i.e. for table view this will redraw one row)
		 * @param {Controller} ac - csi/action/Controller
		 * @return {Deferred}
		 */
		postActionRedrawNode: function(ac) {
			return this.redrawNode(ac.refNode || ac.m_element);
		},

		/**
		 * Same as postActionRedraw, but query for new data rather than use what is in cache.
		 * This was formerly csi.action.postActionRefreshView.
		 * @param {Controller} ac - csi/action/Controller
		 * @return {Deferred}
		 */
		postActionRefresh: function(ac) {
			return this.refreshWidget();
		},

		/**
		 * Same as postActionRedrawData, but query for new data rather than use what is in cache.
		 * @param {Controller} ac - csi/action/Controller
		 * @return {Deferred}
		 */
		postActionRefreshData: function(ac) {
			return this.refreshData();
		},

		postActionRefreshParentView: function(/*Controller*/ac) {
			// summary: refresh page view
			return this.viewParent && this.viewParent.refreshWidget();
		},

		/**
		 * Freeze popup scroll node dimensions, used to prevent resizing flicker
		 * @param {boolean} bFreeze - true to freeze, false to unfreeze
		 */
		freezePopupScrollNode: function(bFreeze) {
			var scrollNode = this.scrollNode;
			if (!this.bPopup || !scrollNode) return;

			html.freezeDomNode(scrollNode, bFreeze);
		}
	});
});
